import { useEffect, useMemo, useRef, useState } from "react";
import ImageCropper from "../../../components/ImageCropper";
import { WebsiteMedia, WebsiteMediaType } from "../../../models/WebsiteModels";
import { CropRegion, cropImageToFile } from "../../../utils/ImageCrop";
import { resolveImageUrl } from "../../../utils/ImageUrl";
import "./WebsiteModal.css";

interface MediaModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  mediaType: WebsiteMediaType;
  item: WebsiteMedia | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: MediaFormValues) => Promise<void>;
}

export interface MediaFormValues {
  title: string;
  description: string;
  sortOrder: number;
  imageFile: File | null;
  beforeImageFile: File | null;
  afterImageFile: File | null;
}

const emptyForm: MediaFormValues = {
  title: "",
  description: "",
  sortOrder: 0,
  imageFile: null,
  beforeImageFile: null,
  afterImageFile: null,
};

// The public banner renders at this aspect ratio; uploads are cropped to fit
// in the modal, so any source image size works. Output is capped at max
// 2400 px wide / 150 KB.
const PROMO_OUTPUT_WIDTH = 2400;
const PROMO_OUTPUT_HEIGHT = 812;
const PROMO_ASPECT_RATIO = PROMO_OUTPUT_WIDTH / PROMO_OUTPUT_HEIGHT;
const PROMO_MAX_BYTES = 150 * 1024;
const BEFORE_AFTER_MAX_BYTES = 150 * 1024;

const validateBeforeAfterFile = (file: File | null) => {
  if (!file) return null;
  if (file.size > BEFORE_AFTER_MAX_BYTES) {
    return `Image must be 150 KB or less (selected file is ${Math.round(file.size / 1024)} KB).`;
  }
  return null;
};

const fromMedia = (item: WebsiteMedia): MediaFormValues => ({
  title: item.title || "",
  description: item.description || "",
  sortOrder: item.sortOrder ?? 0,
  imageFile: null,
  beforeImageFile: null,
  afterImageFile: null,
});

const MediaModal = ({
  isOpen,
  mode,
  mediaType,
  item,
  submitting,
  onClose,
  onSubmit,
}: MediaModalProps) => {
  const [form, setForm] = useState<MediaFormValues>(emptyForm);
  const [fileError, setFileError] = useState<string | null>(null);
  // Latest reported crop for the promo image; rendered to a File on submit.
  const promoCropRef = useRef<CropRegion | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    promoCropRef.current = null;
    if (mode === "create") {
      setForm(emptyForm);
    } else if (item) {
      setForm(fromMedia(item));
    }
    setFileError(null);
  }, [isOpen, mode, mediaType, item]);

  const isPromo = mediaType === "promo";

  const previews = useMemo(
    () => ({
      image: form.imageFile
        ? URL.createObjectURL(form.imageFile)
        : resolveImageUrl(item?.imageUrl),
      before: form.beforeImageFile
        ? URL.createObjectURL(form.beforeImageFile)
        : resolveImageUrl(item?.beforeImageUrl),
      after: form.afterImageFile
        ? URL.createObjectURL(form.afterImageFile)
        : resolveImageUrl(item?.afterImageUrl),
    }),
    [
      form.imageFile,
      form.beforeImageFile,
      form.afterImageFile,
      item?.imageUrl,
      item?.beforeImageUrl,
      item?.afterImageUrl,
    ],
  );

  const blobUrls = useMemo(
    () =>
      [previews.image, previews.before, previews.after].filter(
        (url): url is string => !!url && url.startsWith("blob:"),
      ),
    [previews.image, previews.before, previews.after],
  );

  useEffect(() => {
    return () => {
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [blobUrls]);

  if (!isOpen) return null;

  const setFile = (
    key: "imageFile" | "beforeImageFile" | "afterImageFile",
    file: File | null,
  ) => {
    promoCropRef.current = null;
    setForm((prev) => ({ ...prev, [key]: file }));
    if (key === "beforeImageFile" || key === "afterImageFile") {
      setFileError(validateBeforeAfterFile(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFileError(null);
    let submission = form;
    if (isPromo && form.imageFile) {
      // Render the user's crop to the banner ratio and compress it within
      // the byte budget — takes the place of the old exact-pixel validation.
      const sourceUrl = URL.createObjectURL(form.imageFile);
      try {
        const cropped = await cropImageToFile(
          sourceUrl,
          promoCropRef.current,
          {
            outputWidth: PROMO_OUTPUT_WIDTH,
            aspectRatio: PROMO_ASPECT_RATIO,
            maxBytes: PROMO_MAX_BYTES,
            fileName: `${form.imageFile.name.replace(/\.[^.]+$/, "")}.jpg`,
          },
        );
        submission = { ...form, imageFile: cropped };
      } catch {
        setFileError(
          "This image could not be prepared under 150 KB. Try a different photo.",
        );
        return;
      } finally {
        URL.revokeObjectURL(sourceUrl);
      }
    }
    for (const key of ["beforeImageFile", "afterImageFile"] as const) {
      const file = submission[key];
      if (file) {
        const error = validateBeforeAfterFile(file);
        setFileError(error);
        if (error) return;
      }
    }
    onSubmit(submission);
  };

  const invalid =
    submitting ||
    !!fileError ||
    (mode === "create" &&
      (isPromo
        ? !form.imageFile
        : !form.beforeImageFile && !form.afterImageFile));

  const fileField = (
    key: "imageFile" | "beforeImageFile" | "afterImageFile",
    label: string,
    previewSrc?: string,
    hint?: string,
  ) => (
    <div className="wbm-form-group">
      <label htmlFor={`wbm-${key}`}>{label}</label>
      {previewSrc && (
        <img className="wbm-preview" src={previewSrc} alt={label} />
      )}
      <input
        id={`wbm-${key}`}
        type="file"
        accept="image/*"
        onChange={(e) => setFile(key, e.target.files?.[0] ?? null)}
      />
      {hint && <p className="wbm-hint">{hint}</p>}
    </div>
  );

  return (
    <div className="wbm-overlay">
      <div className="wbm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wbm-modal__header">
          <h3>
            {mode === "create"
              ? isPromo
                ? "Add Promo Image"
                : "Add Before / After"
              : isPromo
                ? "Edit Promo Image"
                : "Edit Before / After"}
          </h3>
          <button
            className="wbm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="wbm-modal__body" onSubmit={handleSubmit}>
          <div className="wbm-form-group">
            <label htmlFor="wbm-title">{isPromo ? "Title" : "Caption"}</label>
            <input
              id="wbm-title"
              type="text"
              value={form.title}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, title: e.target.value }))
              }
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbm-sortOrder">Sort Order</label>
            <input
              id="wbm-sortOrder"
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  sortOrder: Number(e.target.value) || 0,
                }))
              }
            />
          </div>

          {isPromo ? (
            <>
              <div className="wbm-form-group">
                <label htmlFor="wbm-imageFile">Image</label>
                {form.imageFile ? (
                  // The crop box matches the public header's aspect ratio —
                  // it's the same "viewport" visitors see on the site.
                  <ImageCropper
                    imageUrl={previews.image ?? ""}
                    aspectRatio={PROMO_ASPECT_RATIO}
                    onCropChange={(region) => {
                      promoCropRef.current = region;
                    }}
                  />
                ) : (
                  previews.image && (
                    <img
                      className="wbm-preview"
                      src={previews.image}
                      alt=""
                    />
                  )
                )}
                <input
                  id="wbm-imageFile"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setFile("imageFile", e.target.files?.[0] ?? null)
                  }
                />
                <p className="wbm-hint">
                  Banner ratio 2400 : 812, max 150 KB. Drag and zoom the photo
                  — the framed box shows what visitors see in the header.
                </p>
              </div>
              {fileError && <p className="wbm-error">{fileError}</p>}
            </>
          ) : (
            <>
              {fileField("beforeImageFile", "Before Image", previews.before, "Max 150 KB")}
              {fileField("afterImageFile", "After Image", previews.after, "Max 150 KB")}
            </>
          )}

          <div className="wbm-form-group">
            <label htmlFor="wbm-description">Description</label>
            <textarea
              id="wbm-description"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>

          <div className="wbm-modal__actions">
            <button
              type="button"
              className="wbm-btn wbm-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wbm-btn wbm-btn--primary"
              disabled={invalid}
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MediaModal;